import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndPointApi } from '@helpers/endpointapi';
import { lastValueFrom } from 'rxjs';
import { SocketMiddlewareService } from '../socket-middleware.service';
import { SensorCableTagEvents } from 'icreate/common/io-event/websocket-message';
import { SensorCableTag, SensorCableTagPayload, SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { ErrorCodes } from 'icreate/common/excel-import/error';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class SensorCableTagService {
    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    constructor(
        private http: HttpClient,
        private socketService: SocketMiddlewareService,
        private translateService: TranslateService
    ) {
        this.domain = EndPointApi.iApiDomain();
    }

    async getVesselSlots(vesselId: string): Promise<any> {
        return await lastValueFrom(
            this.http.get<any[]>(`${this.domain}/api/sensor-cable`, {
                headers: this.headers,
                params: { vesselId },
            })
        );
    }

    async getSlotChannels(sensorTagSlot: SensorTagSlot): Promise<SensorCableTag[]> {
        return await lastValueFrom(
            this.http.get<SensorCableTag[]>(`${this.domain}/api/sensor-cable/channels`, {
                headers: this.headers,
                params: {
                    fcuNum: sensorTagSlot.fcuNum,
                    fuNum: sensorTagSlot.fuNum,
                    slotNum: sensorTagSlot.no,
                    vesselId: sensorTagSlot.vesselId,
                },
            })
        );
    }

    async update(sensorCableTag: SensorCableTagPayload[]): Promise<SensorCableTag[]> {
        return await lastValueFrom(
            this.http.put<SensorCableTag[]>(`${this.domain}/api/sensor-cable`, sensorCableTag, {
                headers: this.headers,
            })
        );
    }

    sensorCableTagsUpdated$() {
        return this.socketService.receiveMessageObserver(SensorCableTagEvents.UPDATED);
    }

    async importExcelFile(file, vesselId: string, fcuNum: number): Promise<void> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('vesselId', vesselId);
        formData.append('fcuNumString', fcuNum.toString());
        return await lastValueFrom(this.http.post<void>(`${this.domain}/api/sensor-cable/importData`, formData));
    }

    exportExcelFile(vesselId: string, fcuNum: number) {
        return lastValueFrom(
            this.http.get(`${this.domain}/api/sensor-cable/export`, {
                responseType: 'blob',
                params: { fcuNumString: fcuNum.toString(), vesselId },
            })
        );
    }

    getImportErrorMessage(code: ErrorCodes, option): string {
        let text = '';
        switch (code) {
            case ErrorCodes.NO_SLOTS_FOUND:
                text = 'sensor-cable-tag.import-errors.NO_SLOTS_FOUND';
                break;

            case ErrorCodes.NO_SENSOR_CABLE_TAGS_FOUND:
                text = 'sensor-cable-tag.import-errors.NO_SENSOR_CABLE_TAGS_FOUND';
                break;

            case ErrorCodes.SHEET_PARSE_ERROR:
                text = 'sensor-cable-tag.import-errors.SHEET_PARSE_ERROR';
                break;

            case ErrorCodes.SLOT_NOT_FOUND:
                text = 'sensor-cable-tag.import-errors.SLOT_NOT_FOUND';
                break;

            case ErrorCodes.SLOT_NOT_MATCH:
                text = 'sensor-cable-tag.import-errors.SLOT_NOT_MATCH';
                break;

            case ErrorCodes.UNKNOWN_ERROR:
                text = 'sensor-cable-tag.import-errors.UNKNOWN_ERROR';
                break;
            default:
                text = 'sensor-cable-tag.import-errors.UNKNOWN_ERROR';
        }

        return this.translateService.instant(text, option);
    }
}
